// Extraction playground (/demo) API — sandbox that demos the DataLake and
// Case pipelines against one uploaded file. Results live only in PostgreSQL.
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { apiClient } from './client'

export interface PlaygroundChunk {
  content: string
  [k: string]: unknown
}

export interface PlaygroundAnalysis {
  file_type?: string | null
  document_type?: string | null
  confidence?: number | null
  summary?: string | null
  key_findings?: string[]
  content_extraction_preview?: Record<string, unknown> | null
  is_scanned?: boolean | null
}

export interface PlaygroundEntity {
  id: string
  label: string
  display_name?: string | null
  properties?: Record<string, unknown>
}

export interface PlaygroundRelationship {
  type: string
  start_node_id: string
  end_node_id: string
  properties?: Record<string, unknown>
}

export interface GraphPreviewNode {
  id: string
  label: string
  type?: string | null
  properties?: Record<string, unknown>
}

export interface GraphPreviewEdge {
  id: string
  source: string
  target: string
  label?: string | null
  properties?: Record<string, unknown>
}

export interface PlaygroundAttempt {
  id: string
  attempt_no: number
  schema_id: string | null
  schema_name: string | null
  status: string
  error: string | null
  created_at: string | null
  entities: PlaygroundEntity[]
  relationships: PlaygroundRelationship[]
  graph: { nodes: GraphPreviewNode[]; edges: GraphPreviewEdge[] }
}

export interface PlaygroundState {
  id: string
  filename: string
  file_size: number
  file_type: string | null
  created_at: string | null
  lake_status: 'pending' | 'running' | 'done' | 'failed'
  lake_error: string | null
  lake_analysis_result: PlaygroundAnalysis | null
  lake_chunks: PlaygroundChunk[]
  case_attempt_count: number
  max_case_attempts: number
  attempts: PlaygroundAttempt[]
}

export type StepStatus = 'started' | 'finished' | 'failed'

export interface StepEvent {
  type: 'step'
  step: string
  status: StepStatus
  duration_ms?: number
  error?: string
}
export interface ResultEvent {
  type: 'result'
  result: Record<string, unknown>
}
export interface ErrorEvent {
  type: 'error'
  error: string
}
export type RunEvent = StepEvent | ResultEvent | ErrorEvent

export const playgroundApi = {
  get: () => apiClient.get<PlaygroundState | null>('/v1/playground'),
  upload: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return apiClient.post<PlaygroundState>('/v1/playground/upload', fd)
  },
  clear: () => apiClient.delete<void>('/v1/playground'),
}

/** Fetch the active demo document as a Blob (auth-aware). Throws on non-2xx. */
export async function fetchPlaygroundFile(): Promise<Blob> {
  const token = apiClient.getToken()
  const res = await fetch('/api/v1/playground/file', {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    credentials: 'include',
  })
  if (!res.ok) throw new Error(`Failed to load file (${res.status})`)
  return res.blob()
}

/**
 * Stream a demo run over SSE. Resolves when the server closes the stream.
 * Aborts internally on the terminal `result`/`error` event so the underlying
 * library does not attempt to reconnect.
 */
export async function streamRun(
  path: '/v1/playground/run-lake' | '/v1/playground/run-case',
  opts: { params?: Record<string, string>; onEvent: (e: RunEvent) => void }
): Promise<void> {
  const token = apiClient.getToken()
  const qs = opts.params ? `?${new URLSearchParams(opts.params).toString()}` : ''
  const ctrl = new AbortController()
  try {
    await fetchEventSource(`/api${path}${qs}`, {
      method: 'POST',
      signal: ctrl.signal,
      openWhenHidden: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      credentials: 'include',
      onopen: async response => {
        if (response.ok && response.headers.get('content-type')?.includes('text/event-stream'))
          return
        throw new Error(`Stream failed: ${response.status} ${response.statusText}`)
      },
      onmessage: raw => {
        let evt: RunEvent
        try {
          evt = JSON.parse(raw.data) as RunEvent
        } catch {
          return
        }
        opts.onEvent(evt)
        if (evt.type === 'result' || evt.type === 'error') ctrl.abort()
      },
      onerror: err => {
        throw err // prevent the library's auto-retry loop
      },
    })
  } catch (err) {
    // Normal terminal abort — swallow. Real failures surface via an 'error' event.
    if (!ctrl.signal.aborted) throw err
  }
}
